import type {AppProps} from 'next/app'
import React from "react";
import AppLayout from "../components/layout/AppLayout";
import 'bootstrap/dist/css/bootstrap.min.css';
import {GlobalStyles} from "../shared/globalStyles";
import {Router} from 'next/dist/client/router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import {Hydrate, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {SessionProvider} from "next-auth/react";
import {SelectedWishlistProvider} from "../contexts/SelectedWishlistContext";
import {ShowLoginProvider} from "../contexts/ShowLoginContext";
import {LoggedOutCartProvider} from "../contexts/LoggedOutCartContext";
import {LoggedOutWishlistProvider} from "../contexts/LoggedOutWishlistContext";
import {ListingSortingProvider} from "../contexts/ListingSortingContext";
import {UrlAttrFilterQueryProvider} from "../contexts/UrlAttrFilterQueryContext";
import {FilterSelectionExpectedListingCountProvider} from "../contexts/FilterSelectionExpectedListingCount";
import {SearchTermProvider} from "../contexts/SearchTermContext";
import {AxiosInterceptor} from "../shared/httpInterceptor";
import {ClientProvider} from "../contexts/ClientContext";
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {ShouldChangeAddressProvider} from "../contexts/ShouldChangeAddressContext";
import {HasErrorsProvider} from "../contexts/ErrorContext";
import ErrorBoundary from "../components/layout/ErrorBoundary";
import {Provider as JotaiProvider} from 'jotai';
import {CustomThemeProvider} from "../contexts/ThemeContext";
import GTagHeader from "../components/layout/GTagHeader";

NProgress.configure({showSpinner: false});

Router.events.on("routeChangeStart", () => {
  NProgress.start();
});

Router.events.on("routeChangeComplete", () => {
  NProgress.done();
});

Router.events.on("routeChangeError", () => {
  NProgress.done();
});

function MyApp({Component, pageProps}: AppProps) {
  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 10 * (60 * 1000), // 10 mins
        cacheTime: 15 * (60 * 1000), // 15 mins
        // Global check for X-Invoca-Frontend-Device cookie
        // This prevents React Query from fetching data if the cookie is missing
        enabled: typeof window !== 'undefined' ? 
          document.cookie.includes('X-Invoca-Frontend-Device=') : true
      }
    }
  }))

  return (
      <SessionProvider session={pageProps.session}>
        <ClientProvider>
          <CustomThemeProvider>
            <GlobalStyles/>
            <QueryClientProvider client={queryClient}>
              <Hydrate state={pageProps.dehydratedState}>
                <JotaiProvider>
                  <ShowLoginProvider>
                    <ShouldChangeAddressProvider>
                      <SelectedWishlistProvider>
                        <LoggedOutCartProvider>
                          <LoggedOutWishlistProvider>
                            <ListingSortingProvider>
                              <UrlAttrFilterQueryProvider>
                                <FilterSelectionExpectedListingCountProvider>
                                  <SearchTermProvider>
                                    <AxiosInterceptor>
                                      <HasErrorsProvider>
                                        <ErrorBoundary>
                                          <GTagHeader>
                                            <AppLayout>
                                              <ErrorBoundary>
                                                <Component {...pageProps} />
                                              </ErrorBoundary>
                                              <ReactQueryDevtools initialIsOpen={false}/>
                                            </AppLayout>
                                          </GTagHeader>
                                        </ErrorBoundary>
                                        <ToastContainer position={"bottom-center"}
                                                        autoClose={3000}
                                                        theme={"dark"}
                                        />
                                      </HasErrorsProvider>
                                    </AxiosInterceptor>
                                  </SearchTermProvider>
                                </FilterSelectionExpectedListingCountProvider>
                              </UrlAttrFilterQueryProvider>
                            </ListingSortingProvider>
                          </LoggedOutWishlistProvider>
                        </LoggedOutCartProvider>
                      </SelectedWishlistProvider>
                    </ShouldChangeAddressProvider>
                  </ShowLoginProvider>
                </JotaiProvider>
              </Hydrate>
            </QueryClientProvider>
          </CustomThemeProvider>
        </ClientProvider>
      </SessionProvider>
  )
}

export default MyApp
